import React from 'react';
import { TrendingUpIcon, EyeIcon, UsersIcon, PlayIcon, ArrowUpRightIcon, ArrowDownRightIcon } from 'lucide-react';
interface SocialKPICardsProps {
  platform: string;
  engagementRate: string;
  reach?: string;
  followers: string;
  totalViews?: string;
  avgViewsPerVideo?: string;
  yoyEngagement?: number | null;
  yoyReach?: number | null;
  yoyViews?: number | null;
  yoyAvgViews?: number | null;
  yoyFollowers?: number | null;
}
function YoYBadge({
  value,
  isPrimary
}: {
  value: number | null | undefined;
  isPrimary?: boolean;
}) {
  if (value === null || value === undefined) return null;
  const isPositive = value >= 0;
  const Icon = isPositive ? ArrowUpRightIcon : ArrowDownRightIcon;
  if (isPrimary) {
    return <div className={`flex items-center gap-1 text-xs font-semibold ${isPositive ? 'text-emerald-300' : 'text-red-300'}`} data-id="element-175">
        <Icon className="w-3.5 h-3.5" data-id="element-176" />
        <span data-id="element-177">
          {isPositive ? '+' : ''}
          {value}%
        </span>
        <span className="text-primary-foreground/50 font-normal ml-0.5" data-id="element-178">
          vs LY
        </span>
      </div>;
  }
  return <div className={`flex items-center gap-1 text-xs font-semibold ${isPositive ? 'text-emerald-600' : 'text-red-500'}`} data-id="element-179">
      <Icon className="w-3.5 h-3.5" data-id="element-180" />
      <span data-id="element-181">
        {isPositive ? '+' : ''}
        {value}%
      </span>
      <span className="text-muted-foreground font-normal ml-0.5" data-id="element-182">vs LY</span>
    </div>;
}
export function SocialKPICards({
  platform,
  engagementRate,
  reach,
  followers,
  totalViews,
  avgViewsPerVideo,
  yoyEngagement,
  yoyReach,
  yoyViews,
  yoyAvgViews,
  yoyFollowers
}: SocialKPICardsProps) {
  const cards: {
    label: string;
    value: string;
    icon: React.ElementType;
    primary?: boolean;
    yoy?: number | null;
  }[] = [];
  // Engagement Rate is always first (primary) — TikTok uses different label
  cards.push({
    label: platform === 'TikTok' ? 'Engagement Rate by Views' : 'Engagement Rate',
    value: engagementRate,
    icon: TrendingUpIcon,
    primary: true,
    yoy: yoyEngagement
  });
  // Platform-specific second metric
  if (platform === 'Instagram' || platform === 'Facebook') {
    cards.push({
      label: 'Reach',
      value: reach || '0',
      icon: EyeIcon,
      yoy: yoyReach
    });
  }
  if (platform === 'TikTok') {
    cards.push({
      label: 'Avg Views per Video',
      value: avgViewsPerVideo || '0',
      icon: PlayIcon,
      yoy: yoyAvgViews
    });
  }
  // Followers always included
  cards.push({
    label: 'Followers',
    value: followers,
    icon: UsersIcon,
    yoy: yoyFollowers
  });
  const gridCols = cards.length === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-3';
  return <div className={`grid ${gridCols} gap-4`} data-id="element-183">
      {cards.map(card => {
      const IconComp = card.icon;
      if (card.primary) {
        return <div key={card.label} className="bg-primary text-primary-foreground rounded-2xl p-6 shadow-sm flex flex-col justify-between gap-3 h-full" data-id="element-184">
              <div className="flex items-center justify-between" data-id="element-185">
                <span className="text-sm font-medium text-primary-foreground/80" data-id="element-186">
                  {card.label}
                </span>
                <div className="p-2.5 bg-primary-foreground/10 rounded-xl" data-id="element-187">
                  <IconComp className="w-5 h-5 text-primary-foreground" data-id="element-188" />
                </div>
              </div>
              <span className="text-3xl font-bold block" data-id="element-189">{card.value}</span>
              <YoYBadge value={card.yoy} isPrimary data-id="element-190" />
            </div>;
      }
      return <div key={card.label} className="bg-card border border-border rounded-2xl p-6 shadow-sm flex flex-col justify-between gap-3 h-full" data-id="element-191">
            <div className="flex items-center justify-between" data-id="element-192">
              <span className="text-sm font-medium text-muted-foreground" data-id="element-193">
                {card.label}
              </span>
              <div className="p-2.5 bg-muted rounded-xl" data-id="element-194">
                <IconComp className="w-5 h-5 text-foreground" data-id="element-195" />
              </div>
            </div>
            <span className="text-3xl font-bold text-foreground block" data-id="element-196">
              {card.value}
            </span>
            <YoYBadge value={card.yoy} data-id="element-197" />
          </div>;
    })}
    </div>;
}